import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Mail, X, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { formatRoleDisplay, getRoleBadgeColor } from '@/utils/userRoleHelpers';

interface Invitation {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: 'superadmin' | 'member';
  status: 'pending' | 'accepted' | 'expired' | 'cancelled';
  created_at: string;
  expires_at: string;
}

interface InvitationListContainerProps {
  searchTerm: string;
}

export const InvitationListContainer = React.memo(({ searchTerm }: InvitationListContainerProps) => {
  const queryClient = useQueryClient();

  const { data: invitations, isLoading, error } = useQuery({
    queryKey: ['invitations', searchTerm],
    queryFn: async () => {
      let query = supabase
        .from('invitations')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (searchTerm) {
        query = query.or(`first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      return data as Invitation[];
    },
    retry: 3,
    retryDelay: 1000,
    staleTime: 30000, // Keep data fresh for 30 seconds
    gcTime: 300000, // Cache for 5 minutes
  });

  const resendInvitationMutation = useMutation({
    mutationFn: async (invitation: Invitation) => {
      const { data, error } = await supabase.functions.invoke('invite-user', {
        body: {
          email: invitation.email,
          first_name: invitation.first_name,
          last_name: invitation.last_name,
          role: invitation.role
        }
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (_, invitation) => {
      // Update the invitation in all invitation queries instead of invalidating
      queryClient.setQueriesData(
        { queryKey: ['invitations'] },
        (oldData: Invitation[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.map((inv: Invitation) => 
            inv.id === invitation.id ? { ...inv, created_at: new Date().toISOString() } : inv
          );
        }
      );
      toast.success('Invitation resent successfully');
    },
    onError: (error: Error) => {
      toast.error('Failed to resend invitation');
      console.error('Error:', error);
    },
  });

  const cancelInvitationMutation = useMutation({
    mutationFn: async (invitationId: string) => {
      const { error } = await supabase
        .from('invitations')
        .update({ status: 'cancelled' })
        .eq('id', invitationId);
      
      if (error) throw error;
      return invitationId;
    },
    onSuccess: (invitationId) => {
      // Update the invitation status in all invitation queries instead of invalidating
      queryClient.setQueriesData(
        { queryKey: ['invitations'] },
        (oldData: Invitation[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.map((inv: Invitation) => 
            inv.id === invitationId ? { ...inv, status: 'cancelled' } : inv
          );
        }
      );
      toast.success('Invitation cancelled successfully');
    },
    onError: (error: Error) => {
      toast.error('Failed to cancel invitation');
      console.error('Error:', error);
    },
  });

  const getStatusBadge = (invitation: Invitation) => {
    const isExpired = new Date(invitation.expires_at) < new Date();
    const actualStatus = isExpired ? 'expired' : invitation.status;
    
    switch (actualStatus) {
      case 'pending':
        return <Badge variant="outline" className="text-yellow-600 border-yellow-200">Pending</Badge>;
      case 'accepted':
        return <Badge variant="outline" className="text-green-600 border-green-200">Accepted</Badge>;
      case 'expired':
        return <Badge variant="outline" className="text-red-600 border-red-200">Expired</Badge>;
      case 'cancelled':
        return <Badge variant="outline" className="text-gray-600 border-gray-200">Cancelled</Badge>;
      default:
        return <Badge variant="outline">{actualStatus}</Badge>;
    }
  };

  const canResend = (invitation: Invitation) => {
    const isExpired = new Date(invitation.expires_at) < new Date();
    return invitation.status === 'pending' || isExpired;
  };

  const canCancel = (invitation: Invitation) => {
    return invitation.status === 'pending' && new Date(invitation.expires_at) > new Date();
  };

  if (isLoading && !invitations) {
    return <div>Loading invitations...</div>;
  }

  if (error) {
    return (
      <div className="text-red-600 p-4 border border-red-200 rounded-lg">
        <h3 className="font-semibold">Error loading invitations</h3>
        <p className="text-sm mt-1">{error.message}</p>
      </div>
    );
  }

  const filteredInvitations = invitations || [];

  if (filteredInvitations.length > 0) {
    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Sent</TableHead>
            <TableHead>Expires</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredInvitations.map((invitation) => (
            <TableRow key={invitation.id}>
              <TableCell className="font-medium">
                {invitation.first_name} {invitation.last_name}
              </TableCell>
              <TableCell>{invitation.email}</TableCell>
              <TableCell>
                <Badge className={getRoleBadgeColor(invitation.role)}>
                  {formatRoleDisplay(invitation.role)}
                </Badge>
              </TableCell>
              <TableCell>
                {getStatusBadge(invitation)}
              </TableCell>
              <TableCell>
                {new Date(invitation.created_at).toLocaleDateString()}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-1">
                  <Clock className="h-3 w-3 text-muted-foreground" />
                  {new Date(invitation.expires_at).toLocaleDateString()}
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  {canResend(invitation) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => resendInvitationMutation.mutate(invitation)}
                      disabled={resendInvitationMutation.isPending}
                    >
                      <Mail className="h-4 w-4" />
                    </Button>
                  )}
                  {canCancel(invitation) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => cancelInvitationMutation.mutate(invitation.id)}
                      disabled={cancelInvitationMutation.isPending}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  return (
    <div className="text-center py-8">
      <Mail className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
      <h3 className="text-lg font-semibold mb-2">No invitations found</h3>
      <p className="text-muted-foreground">
        {searchTerm ? 'Try adjusting your search criteria' : 'No pending invitations at the moment'}
      </p>
    </div>
  );
});

InvitationListContainer.displayName = 'InvitationListContainer';